import React from "react";
import PageShell from "@/components/layout/PageShell";
import { ARCHETYPES } from "@/components/assessment/assessmentData";
import { getEvidenceMapping } from "@/components/assessment/evidenceMapping";
import { getEducationalModules } from "@/components/assessment/educationalModules";
import {
  buildImplementationPlan,
  DEFAULT_IMPLEMENTATION_ANSWERS,
} from "@/components/assessment/implementationPlan";

const CHECKLIST = [
  "Does it identify the state clearly?",
  "Does it state what the person needs?",
  "Is intensity explicit and relative?",
  "Are options practical?",
  "Are individual, coached, and social choices included?",
  "Does it explain expected effects and adaptations?",
  "Does it locate the supported loop pressure and interruption point?",
  "Does it provide an immediate input, daily anchor, and one-week plan?",
  "Are readiness overrides and calendar sessions coherent?",
  "Is anything defensive, repetitive, or contradictory?",
  "Does the PDF contain the same result content?",
];

const PROFILE_DIMENSIONS = {
  "high-activation": { state: 2.2, inputs: 2.8, capacity: 3.1 },
  "reduced-capacity": { state: 2.9, inputs: 2.7, capacity: 2.2 },
  "recovery-focus": { state: 3.1, inputs: 3.8, capacity: 3.5 },
  "regulation-building": { state: 3.4, inputs: 3.5, capacity: 3.4 },
  "growth-performance": { state: 4.2, inputs: 4.1, capacity: 4.3 },
};

const ResultsGallery = () => (
  <PageShell>
    <main className="forr-canvas min-h-screen pt-28 pb-24">
      <div className="max-w-[1180px] mx-auto px-6 sm:px-10">
        <div className="forr-overline">Development only</div>
        <h1 className="mt-4 font-display forr-text-ink text-4xl sm:text-6xl font-medium tracking-tight">
          Assessment results gallery
        </h1>
        <p className="mt-5 max-w-3xl forr-text-soft font-light leading-relaxed">
          Every adaptive state, intensity, activity combination, framework explanation,
          and Adaptive Physiological Summary is shown here for one-pass review.
        </p>

        <section className="mt-10 rounded-[20px] p-6 forr-surface" style={{ border: "1px solid var(--forr-line)" }}>
          <div className="forr-overline mb-4">Review checklist</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {CHECKLIST.map((item) => (
              <label key={item} className="flex items-start gap-3 text-sm forr-text-ink">
                <input type="checkbox" className="mt-1" />
                <span>{item}</span>
              </label>
            ))}
          </div>
        </section>

        <div className="mt-14 space-y-16">
          {Object.values(ARCHETYPES).map((profile) => {
            const mapping = getEvidenceMapping(profile.key);
            const answers = {
              ...DEFAULT_IMPLEMENTATION_ANSWERS,
              behaviors:
                profile.key === "high-activation"
                  ? ["avoidance", "scrolling", "overwork"]
                  : [],
              current: {
                ...DEFAULT_IMPLEMENTATION_ANSWERS.current,
                states:
                  profile.key === "high-activation"
                    ? ["angry-irritable"]
                    : profile.key === "reduced-capacity"
                      ? ["sad-low"]
                      : profile.key === "growth-performance"
                        ? ["positive-energized"]
                        : ["calm-steady"],
              },
              schedule: {
                ...DEFAULT_IMPLEMENTATION_ANSWERS.schedule,
                familiarActivities:
                  profile.key === "reduced-capacity"
                    ? ["none-unsure"]
                    : profile.key === "growth-performance"
                      ? ["aerobic", "resistance", "anaerobic", "sport-competition"]
                      : ["aerobic", "resistance"],
                competitionResponse:
                  profile.key === "high-activation"
                    ? "motivates-overactivates"
                    : profile.key === "recovery-focus"
                      ? "pressure-discouraging"
                      : "focus-engage",
              },
            };
            const dimensions = PROFILE_DIMENSIONS[profile.key];
            const plan = buildImplementationPlan({
              profile,
              mapping,
              answers,
              now: new Date(2026, 6, 30),
            });
            const educationalModules = getEducationalModules({
              profileKey: profile.key,
              dimensions,
              answers,
            });
            return (
              <article key={profile.key} className="rounded-[28px] p-6 sm:p-9 forr-surface" style={{ border: "1px solid var(--forr-line)" }}>
                <div className="forr-overline">{profile.key}</div>
                <h2 className="mt-3 font-display text-3xl forr-text-ink">{profile.title}</h2>
                <h3 className="mt-4 font-display text-xl forr-text-teal-deep">{mapping.stateName}</h3>
                <p className="mt-3 text-sm forr-text-soft leading-relaxed">{mapping.stateDescription}</p>

                <div className="mt-6 grid md:grid-cols-3 gap-4">
                  <div className="rounded-[16px] p-4" style={{ backgroundColor: "#111315", color: "#F4F1EA" }}>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#AEB4B7] mb-2">Dependency Loop</div>
                    <p className="text-sm">{plan.loops.dependencyLabel}</p>
                    <p className="mt-2 text-xs text-[#AEB4B7] leading-relaxed">{plan.loops.interruptionPoint}</p>
                  </div>
                  <div className="rounded-[16px] p-4" style={{ backgroundColor: "var(--forr-teal-soft)" }}>
                    <div className="forr-overline mb-2">Right now</div>
                    <p className="text-sm">{plan.immediate.title}</p>
                    <p className="mt-2 text-xs forr-text-soft leading-relaxed">{plan.immediate.duration}</p>
                  </div>
                  <div className="rounded-[16px] p-4 forr-canvas-warm">
                    <div className="forr-overline mb-2">One-week change</div>
                    <p className="text-sm">{plan.week.title}</p>
                    <p className="mt-2 text-xs forr-text-soft leading-relaxed">
                      {plan.week.frequency} sessions · {plan.anchor.slotLabel} at {plan.anchor.time}
                    </p>
                  </div>
                </div>

                <div className="mt-6 grid md:grid-cols-3 gap-4">
                  <div className="rounded-[16px] p-4 forr-canvas-warm">
                    <div className="forr-overline mb-2">Intensity</div>
                    <p className="text-sm">{mapping.recommendedInput.intensity}</p>
                    <p className="mt-2 text-xs forr-text-soft leading-relaxed">{mapping.input.intensityDefinition}</p>
                  </div>
                  <div className="rounded-[16px] p-4 forr-canvas-warm">
                    <div className="forr-overline mb-2">Framework</div>
                    <p className="text-xs forr-text-soft leading-relaxed">{mapping.frameworkExplanation}</p>
                  </div>
                  <div className="rounded-[16px] p-4 forr-canvas-warm">
                    <div className="forr-overline mb-2">Expected adaptations</div>
                    <ul className="space-y-1">{mapping.input.adaptations.map((a) => <li key={a} className="text-xs">— {a}</li>)}</ul>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="forr-overline mb-3">Every activity combination</div>
                  <div className="grid lg:grid-cols-3 gap-4">
                    {mapping.activities.map((activity) => (
                      <div key={activity.id} className="rounded-[16px] p-4" style={{ border: "1px solid var(--forr-line-soft)" }}>
                        <h4 className="font-display text-lg">{activity.name}</h4>
                        <p className="mt-2 text-xs forr-text-soft">{activity.intensity}</p>
                        <p className="mt-3 text-xs leading-relaxed">{activity.examples}</p>
                        <p className="mt-3 text-xs leading-relaxed">{activity.benefits}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 rounded-[16px] p-5" style={{ backgroundColor: "var(--forr-teal-soft)" }}>
                  <div className="forr-overline mb-3">Adaptive Physiological Summary</div>
                  <p className="text-sm leading-relaxed">{mapping.professional.interpretation}</p>
                  <p className="mt-3 text-sm leading-relaxed">{mapping.professional.matchedInput}</p>
                  <p className="mt-3 text-xs forr-text-soft">{mapping.professional.acute}</p>
                  <p className="mt-2 text-xs forr-text-soft">{mapping.professional.repeated}</p>
                </div>

                <div className="mt-6">
                  <div className="forr-overline mb-3">Profile-aware physiology modules</div>
                  <div className="grid lg:grid-cols-2 gap-4">
                    {educationalModules.map((module) => (
                      <div key={module.id} className="rounded-[16px] p-4" style={{ border: "1px solid var(--forr-line-soft)" }}>
                        <h4 className="font-display text-lg">{module.title}</h4>
                        <p className="mt-2 text-xs forr-text-soft leading-relaxed">{module.scoreContext}</p>
                        <p className="mt-3 text-xs leading-relaxed">{module.profileRelevance}</p>
                        <p className="mt-3 text-xs leading-relaxed"><strong>CNS & biology: </strong>{module.biology}</p>
                        <p className="mt-3 text-xs leading-relaxed"><strong>Homeostasis: </strong>{module.homeostasis}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </main>
  </PageShell>
);

export default ResultsGallery;
